import React from 'react';
import { Container } from '../components/common/Container';
import { CalendarDaysIcon, CheckCircleIcon, EnvelopeIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { BOOKING_DURATION_MINUTES, BOOKING_URL, CONTACT_EMAIL } from '../config/contact';

export const Contact: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="bg-brand-blue py-20">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Book a {BOOKING_DURATION_MINUTES}-minute workflow review
            </h1>
            <p className="text-xl text-brand-blue-100">
              Bring the part of your property tax appeal process that costs your
              team the most time. We’ll review it together and show where Appeal
              Advantage may help.
            </p>
          </div>
        </Container>
      </section>
      
      <section className="py-20 bg-white">
        <Container>
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-start">
            <div>
              <p className="text-sm font-bold tracking-[0.18em] text-brand-red mb-3">A SHORT, USEFUL CONVERSATION</p>
              <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-6">
                Bring the workflow that costs your team the most time
              </h2>
              <p className="text-lg text-brand-white-700 mb-8 max-w-2xl">
                We’ll use your current process as the starting point, identify the
                friction, and show where Appeal Advantage can reduce manual county-data
                work, clarify appeal ownership, and keep upcoming deadlines visible.
              </p>

              <div className="space-y-4 mb-9">
                {[
                  'Where county values or assessment data enter your process',
                  'How your team tracks stages, ownership, and next actions',
                  'Which filing, hearing, and response deadlines create risk',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircleIcon className="h-6 w-6 flex-shrink-0 text-brand-red" />
                    <span className="text-brand-white-800">{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-lg bg-brand-red px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-brand-red-600"
                >
                  Book your {BOOKING_DURATION_MINUTES}-minute review
                </a>
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=Appeal%20Advantage%20question`}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-brand-blue px-8 py-4 text-lg font-semibold text-brand-blue transition-colors hover:bg-brand-blue-50"
                >
                  <EnvelopeIcon className="h-5 w-5" />
                  Email our team
                </a>
              </div>
            </div>

            <div className="rounded-2xl bg-brand-blue p-8 text-white shadow-xl lg:p-10">
              <h3 className="text-2xl font-bold text-white mb-7">What to expect from your review</h3>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <CalendarDaysIcon className="h-7 w-7 flex-shrink-0 text-red-300" />
                  <div>
                    <h4 className="font-bold text-white">Start with your process</h4>
                    <p className="mt-1 text-brand-blue-100">We’ll focus on the steps, handoffs, and deadlines your team manages today.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <VideoCameraIcon className="h-7 w-7 flex-shrink-0 text-red-300" />
                  <div>
                    <h4 className="font-bold text-white">See only what matters</h4>
                    <p className="mt-1 text-brand-blue-100">We’ll show the parts of Appeal Advantage that relate to the workflow you want to improve.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <CheckCircleIcon className="h-7 w-7 flex-shrink-0 text-red-300" />
                  <div>
                    <h4 className="font-bold text-white">Leave with a clear next step</h4>
                    <p className="mt-1 text-brand-blue-100">You’ll know where Appeal Advantage fits—and where it may not.</p>
                  </div>
                </div>
              </div>
              <div className="mt-8 border-t border-brand-blue-400 pt-6 text-sm text-brand-blue-100">
                Questions first? Email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-white underline decoration-red-300 underline-offset-4">
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};
